import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Send, MessageCircle, Trash2, AtSign, Paperclip, Download, File as FileIcon, ListTodo } from "lucide-react";

const AVATAR_COLORS = ["#0044FF", "#7C3AED", "#0D9488", "#EA580C", "#DB2777", "#059669", "#B45309"];
const colorFor = (name) => AVATAR_COLORS[(name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function MessageText({ text, mentions, tasks, onTask }) {
  const names = (mentions || []).map((m) => m.person).filter(Boolean);
  const taskList = useMemo(() => (tasks || [])
    .filter((t) => (t.title || "").trim().length >= 4)
    .sort((a, b) => b.title.length - a.title.length)
    .slice(0, 400), [tasks]);
  const parts = useMemo(() => {
    const pats = [];
    if (names.length) pats.push(`@(?:${names.map(escapeRe).join("|")})`);
    if (taskList.length) pats.push(`(?:${taskList.map((t) => escapeRe(t.title.trim())).join("|")})`);
    if (!pats.length) return null;
    try { return text.split(new RegExp(`(${pats.join("|")})`, "gi")); } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, mentions, taskList]);
  if (!parts) return <p className="text-sm whitespace-pre-wrap break-words">{text}</p>;
  const lowerNames = names.map((n) => n.toLowerCase());
  const byTitle = {};
  taskList.forEach((t) => { byTitle[t.title.trim().toLowerCase()] = t; });
  return (
    <p className="text-sm whitespace-pre-wrap break-words">
      {parts.map((p, i) => {
        if (p.startsWith("@") && lowerNames.includes(p.slice(1).toLowerCase()))
          return <span key={i} className="font-bold underline underline-offset-2">{p}</span>;
        const task = byTitle[(p || "").trim().toLowerCase()];
        if (task)
          return (
            <button key={i} type="button" onClick={() => onTask?.(task)}
              title={`Open this task · ${task.percent_complete || 0}% · ${(task.status || "").replace("_", " ")}`}
              className="inline font-semibold underline underline-offset-2 decoration-dotted hover:opacity-75 text-left align-baseline"
              data-testid={`pit-task-link-${task.id}`}>
              {p}
            </button>
          );
        return <span key={i}>{p}</span>;
      })}
    </p>
  );
}

function Attachment({ msg }) {
  const [url, setUrl] = useState(null);
  const att = msg.attachment;
  useEffect(() => {
    let obj;
    if (att?.is_image) {
      api.get(`/chat/${msg.id}/file`, { responseType: "blob" }).then(({ data }) => {
        obj = URL.createObjectURL(data);
        setUrl(obj);
      }).catch(() => {});
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [msg.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try {
      const { data } = await api.get(`/chat/${msg.id}/file`, { responseType: "blob" });
      const obj = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = obj; a.download = att.filename || "file"; a.click();
      URL.revokeObjectURL(obj);
    } catch { toast.error("Could not download the file"); }
  };

  if (!att) return null;
  const size = att.size > 1048576 ? `${(att.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(att.size / 1024))} KB`;
  if (att.is_image) {
    return (
      <div className="mt-1">
        {url ? (
          <img src={url} alt={att.filename} onClick={() => window.open(url, "_blank")}
            className="max-h-64 max-w-full rounded-lg cursor-zoom-in border border-black/10" data-testid={`pit-image-${msg.id}`} />
        ) : (
          <div className="h-24 w-40 rounded-lg bg-black/10 animate-pulse" data-testid={`pit-image-loading-${msg.id}`} />
        )}
        <button onClick={download} className="mt-0.5 inline-flex items-center gap-1 text-[11px] opacity-80 hover:opacity-100 hover:underline" data-testid={`pit-download-${msg.id}`}>
          <Download className="h-3 w-3" /> {att.filename} · {size}
        </button>
      </div>
    );
  }
  return (
    <button onClick={download} className="mt-1 flex items-center gap-2 rounded-lg border border-black/10 bg-black/5 px-2.5 py-1.5 text-left hover:bg-black/10 transition-colors" data-testid={`pit-file-${msg.id}`}>
      <FileIcon className="h-4 w-4 shrink-0" />
      <span className="min-w-0">
        <span className="block text-xs font-semibold truncate max-w-[220px]">{att.filename}</span>
        <span className="block text-[10px] opacity-70">{size} · click to download</span>
      </span>
      <Download className="h-3.5 w-3.5 shrink-0 opacity-70" />
    </button>
  );
}

export default function OrchestraPit({ projectId, onRead, onOpenTask }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [projTasks, setProjTasks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const firstLoad = useRef(true);

  const markRead = () => {
    api.post(`/projects/${projectId}/chat/read`).then(() => onRead?.()).catch(() => {});
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).slice(0, 5);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} is over the 25 MB limit`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/projects/${projectId}/chat/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMessages((m) => [...(m || []), data]);
      } catch { toast.error(`Could not upload ${f.name}`); }
    }
    setUploading(false);
    markRead();
  };

  const load = () => api.get(`/projects/${projectId}/chat`).then(({ data }) => {
    setMessages((prev) => {
      if (prev && data.length > prev.length) markRead();
      return data;
    });
  }).catch(() => setMessages((m) => m || []));

  useEffect(() => {
    firstLoad.current = true;
    load();
    markRead();
    api.get("/mention-candidates").then(({ data }) => setCandidates(data)).catch(() => {});
    api.get(`/projects/${projectId}/tasks`).then(({ data }) => setProjTasks(data)).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!messages) return;
    bottomRef.current?.scrollIntoView({ behavior: firstLoad.current ? "auto" : "smooth" });
    firstLoad.current = false;
  }, [messages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const mentionMatch = text.match(/@([^@\n]{0,30})$/);
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionOptions = mentionQuery !== null
    ? candidates.filter((n) => n.toLowerCase().startsWith(mentionQuery)).slice(0, 6)
    : [];
  const pickMention = (name) => setText((b) => b.replace(/@([^@\n]{0,30})$/, `@${name} `));

  const taskMatch = text.match(/#([^#\n]{0,40})$/);
  const taskQuery = taskMatch ? taskMatch[1].toLowerCase() : null;
  const taskOptions = taskQuery !== null
    ? projTasks.filter((t) => (t.title || "").toLowerCase().includes(taskQuery)).slice(0, 6)
    : [];
  const pickTask = (title) => setText((b) => b.replace(/#([^#\n]{0,40})$/, `${title} `));

  const send = async () => {
    const val = text.trim();
    if (!val) return;
    setSending(true);
    try {
      const mentions = candidates.filter((n) => val.toLowerCase().includes("@" + n.toLowerCase()));
      const { data } = await api.post(`/projects/${projectId}/chat`, { text: val, mentions });
      setMessages((m) => [...(m || []), data]);
      setText("");
      markRead();
      if (mentions.length) toast.success(`Mentioned ${mentions.join(", ")} — it'll show on their card`);
    } catch (e) { toast.error("Could not send the message"); }
    finally { setSending(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/chat/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
    } catch (e) { toast.error("Could not delete"); }
  };

  if (messages === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  let lastDay = "";
  return (
    <div className="border border-border rounded-lg flex flex-col h-[calc(100vh-320px)] min-h-[420px]" data-testid="orchestra-pit">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2.5">
        <MessageCircle className="h-4 w-4 text-primary" />
        <div>
          <p className="font-display font-bold text-sm">Orchestra Pit</p>
          <p className="text-[11px] text-muted-foreground">Team chat room for this project — type <b>@</b> to mention someone (it lands on their card).</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 relative"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
        data-testid="pit-messages">
        {dragOver && (
          <div className="absolute inset-2 z-10 rounded-lg border-2 border-dashed border-primary bg-primary/5 flex items-center justify-center pointer-events-none" data-testid="pit-drop-overlay">
            <p className="text-sm font-bold text-primary flex items-center gap-2"><Paperclip className="h-4 w-4" /> Drop files to share with the team</p>
          </div>
        )}
        {messages.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10" data-testid="pit-empty">No messages yet — kick off the conversation below, or drop a file to share it.</p>
        )}
        {messages.map((m) => {
          const day = (m.created_at || "").slice(0, 10);
          const showDay = day !== lastDay;
          lastDay = day;
          const mine = m.author_id === user?.id;
          const initials = (m.author_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
          return (
            <div key={m.id}>
              {showDay && <p className="text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground my-3">{day}</p>}
              <div className={`group flex items-start gap-2.5 ${mine ? "flex-row-reverse" : ""}`} data-testid={`pit-msg-${m.id}`}>
                <span className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ background: colorFor(m.author_name) }}>{initials}</span>
                <div className={`max-w-[70%] rounded-xl px-3.5 py-2 ${mine ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary rounded-tl-sm"}`}>
                  <p className={`text-[10px] font-bold mb-0.5 ${mine ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {m.author_name} · {(m.created_at || "").slice(11, 16)}
                  </p>
                  <MessageText text={m.text} mentions={m.mentions} tasks={projTasks} onTask={onOpenTask} />
                  {m.attachment && <Attachment msg={m} />}
                </div>
                {mine && (
                  <button onClick={() => remove(m.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity self-center" title="Delete this message" data-testid={`pit-delete-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-3 border-t border-border relative">
        {mentionOptions.length > 0 && (
          <div className="absolute bottom-full left-4 mb-1 w-64 rounded-lg border border-border bg-popover shadow-lg overflow-hidden z-10" data-testid="pit-mention-dropdown">
            <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><AtSign className="h-3 w-3" /> Mention someone</p>
            {mentionOptions.map((n) => (
              <button key={n} onClick={() => pickMention(n)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors"
                data-testid={`pit-mention-option-${n.toLowerCase().replace(/\s+/g, "-")}`}>
                @{n}
              </button>
            ))}
          </div>
        )}
        {taskOptions.length > 0 && mentionOptions.length === 0 && (
          <div className="absolute bottom-full left-4 mb-1 w-80 rounded-lg border border-border bg-popover shadow-lg overflow-hidden z-10" data-testid="pit-task-dropdown">
            <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ListTodo className="h-3 w-3" /> Link a task</p>
            {taskOptions.map((t) => (
              <button key={t.id} onClick={() => pickTask(t.title)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors flex items-center gap-2"
                data-testid={`pit-task-option-${t.id}`}>
                <span className="truncate flex-1">{t.is_milestone ? "◆ " : ""}{t.title}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{t.percent_complete || 0}%</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="pit-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="h-9 w-9 shrink-0 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-50"
            title="Attach a file or image (25 MB max)" data-testid="pit-attach-button">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <textarea
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && mentionOptions.length === 0 && taskOptions.length === 0) { e.preventDefault(); send(); } }}
            placeholder="Message the team… @ to mention, # to link a task, drop files to share"
            className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none max-h-32"
            data-testid="pit-input"
          />
          <Button className="gap-1.5 shrink-0" onClick={send} disabled={sending || !text.trim()} data-testid="pit-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </Button>
        </div>
      </div>
    </div>
  );
}
